var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { Button } from '../../../atoms/Button/Button';
import './UndoButtonStyles.scss';
const baseClass = 'qmb-ui-undo-button';
/**
 * Calculates remaining seconds until expiration.
 * Returns 0 if already expired.
 */
function getRemainingSeconds(expiresAt) {
    if (!expiresAt)
        return 1800;
    const expiryTime = new Date(expiresAt).getTime();
    if (Number.isNaN(expiryTime))
        return 1800;
    const now = Date.now();
    const remaining = Math.max(0, Math.floor((expiryTime - now) / 1000));
    return remaining;
}
/**
 * Formats seconds as MM:SS string.
 */
function formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}
/**
 * UndoButton Component
 *
 * Renders an inline undo button with countdown timer for write operations.
 * Automatically disables when the undo window expires.
 *
 * @example
 * ```tsx
 * <UndoButton
 *   undoData={{ action_id: '123', summary: 'Created inspection', undo_available: true, expires_at: '...', undo_window_minutes: 30 }}
 *   onUndo={(actionId) => submitUndo({ action_id: actionId })}
 * />
 * ```
 *
 * @version 1.0.0
 * @since 2026-03-04
 */
export function UndoButton({ undoData, onUndo }) {
    const { action_id, summary, undo_available, expires_at, undone } = undoData;
    const [remainingSeconds, setRemainingSeconds] = useState(() => getRemainingSeconds(expires_at));
    const [isUndoing, setIsUndoing] = useState(false);
    // IP-15624: seeded, not hardcoded false. `expires_at` is frozen at write time
    // and replayed verbatim on reload, so an operation the user already undid came
    // back after a refresh as a live "Undo" with a running countdown; clicking it
    // flipped to "Unavailable" with "already been undone" (QA, 2026-09-15).
    // `undo_available: false` is not a substitute — it renders a disabled button
    // still labelled "Undo", and is only allowed to hide the control when the
    // window has ALSO lapsed (see the isExpired guard below).
    const [isUndone, setIsUndone] = useState(() => undone === true);
    const [isRefused, setIsRefused] = useState(false);
    // A state initializer runs once per mount, and this component is not always
    // remounted when the data changes: AIAgentMessage keys each BlockWrapper by
    // `block_id`, and BlockWrapper renders UndoButton without a key of its own, so
    // an existing block that gains `undone: true` reuses this instance. Without
    // this the seed above would be ignored and the row would keep offering an undo
    // that has already happened. One-way on purpose — a reversal does not un-occur,
    // and the local click path owns the false -> true transition.
    useEffect(() => {
        if (undone === true)
            setIsUndone(true);
    }, [undone]);
    // Countdown timer effect
    useEffect(() => {
        if (remainingSeconds <= 0 || isUndone)
            return undefined;
        const timer = setInterval(() => {
            setRemainingSeconds((prev) => {
                const newVal = prev - 1;
                if (newVal <= 0) {
                    clearInterval(timer);
                    return 0;
                }
                return newVal;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [remainingSeconds, isUndone]);
    const handleUndo = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!undo_available || remainingSeconds <= 0 || isUndoing || isUndone || isRefused)
            return;
        setIsUndoing(true);
        try {
            yield onUndo(action_id);
            setIsUndone(true);
        }
        catch (error) {
            setIsUndoing(false);
            // A 4xx is the server DECLINING — not undoable, window closed, already
            // undone. Re-offering the button invites the user to click into the same
            // refusal forever: QA fired three identical 422s at a bulk
            // cancel_inspection because this handler reset straight back to a live
            // "Undo" and said nothing (IP-15263). The orchestrator now narrates the
            // reason into the conversation, so the button's only remaining job is to
            // stop pretending a retry might work.
            //
            // 5xx and network failures stay retryable — those genuinely can succeed
            // on a second attempt, and disabling them would strand a recoverable undo.
            const status = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status;
            if (typeof status === 'number' && status >= 400 && status < 500) {
                setIsRefused(true);
            }
        }
    }), [undo_available, remainingSeconds, isUndoing, isUndone, isRefused, onUndo, action_id]);
    const isExpired = remainingSeconds <= 0;
    const isDisabled = !undo_available || isExpired || isUndoing || isUndone || isRefused;
    // Don't render if expired and no longer available.
    //
    // `!isUndone` is part of the condition because expires_at is frozen at write
    // time: reopen the conversation an hour later and an undone operation is also
    // expired, so without this the "Undone" pill would vanish in exactly the case
    // it exists for — a user checking back on work they already reverted
    // (IP-15624). An undone action is a settled outcome worth showing, not a
    // stale affordance worth hiding.
    if (isExpired && !undo_available && !isUndone) {
        return null;
    }
    // Compute button text based on state priority: undone > undoing > refused > expired > default
    let buttonLabel = 'Undo';
    if (isUndone) {
        buttonLabel = 'Undone';
    }
    else if (isUndoing) {
        buttonLabel = 'Undoing...';
    }
    else if (isRefused) {
        // Never "Undone" — nothing was reverted. The explanation arrives as a
        // message in the conversation; this label only has room to say it did not
        // happen.
        buttonLabel = 'Unavailable';
    }
    else if (isExpired) {
        buttonLabel = 'Expired';
    }
    let ariaLabel = `Undo: ${summary}`;
    if (isUndone) {
        ariaLabel = 'Action has been undone';
    }
    else if (isRefused) {
        ariaLabel = 'Undo is not available for this operation — nothing was changed';
    }
    else if (isExpired) {
        ariaLabel = 'Undo no longer available';
    }
    return (React.createElement("div", { className: classNames(baseClass, {
            [`${baseClass}--expired`]: isExpired,
            [`${baseClass}--undone`]: isUndone
        }) },
        React.createElement("div", { className: `${baseClass}__content` },
            React.createElement("span", { className: `${baseClass}__icon` },
                React.createElement("i", { className: "fa-light fa-rotate-left", "aria-hidden": "true" })),
            React.createElement("span", { className: `${baseClass}__summary` }, summary),
            !isExpired && !isUndoing && !isUndone && (React.createElement("span", { className: `${baseClass}__timer`, "aria-label": `Undo available for ${formatTime(remainingSeconds)}` }, formatTime(remainingSeconds)))),
        React.createElement(Button, { variant: "secondary", onClick: () => {
                void handleUndo();
            }, disabled: isDisabled, className: `${baseClass}__action`, "aria-label": ariaLabel }, buttonLabel)));
}
